import clsx from 'clsx'
import type { Game } from '../../types'

interface GameCardProps {
  game: Game
}

export default function GameCard({ game }: GameCardProps) {
  const isLive = game.status === 'IN_PROGRESS'
  const isFinal = game.status === 'FINAL'

  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      {/* Status badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={clsx(
          'text-xs font-medium px-2 py-1 rounded-full',
          isLive && 'bg-red-100 text-red-700',
          isFinal && 'bg-gray-100 text-gray-600',
          game.status === 'SCHEDULED' && 'bg-blue-100 text-blue-700',
          game.status === 'POSTPONED' && 'bg-yellow-100 text-yellow-700'
        )}>
          {isLive && <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-live" />}
          {formatStatus(game)}
        </span>
        <span className="text-xs text-gray-500">{game.sport}</span>
      </div>

      {/* Teams and scores */}
      <div className="space-y-3">
        <TeamRow
          team={game.awayTeam}
          score={game.score?.awayScore}
          isWinning={game.score && game.score.awayScore > game.score.homeScore}
          showScore={isLive || isFinal}
        />
        <TeamRow
          team={game.homeTeam}
          score={game.score?.homeScore}
          isWinning={game.score && game.score.homeScore > game.score.awayScore}
          showScore={isLive || isFinal}
        />
      </div>

      {/* Game info */}
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        {game.venue && <span>{game.venue}</span>}
        {game.broadcast && (
          <>
            <span className="mx-2">·</span>
            <span>{game.broadcast}</span>
          </>
        )}
      </div>
    </div>
  )
}

interface TeamRowProps {
  team: Game['homeTeam']
  score?: number
  isWinning?: boolean
  showScore: boolean
}

function TeamRow({ team, score, isWinning, showScore }: TeamRowProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {team.logo ? (
          <img src={team.logo} alt={team.name} className="w-8 h-8" />
        ) : (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
            {team.abbreviation}
          </div>
        )}
        <span className={clsx(
          'font-medium',
          isWinning ? 'text-gray-900' : 'text-gray-600'
        )}>
          {team.name}
        </span>
      </div>
      {showScore && (
        <span className={clsx(
          'text-xl font-bold tabular-nums',
          isWinning ? 'text-gray-900' : 'text-gray-500'
        )}>
          {score ?? 0}
        </span>
      )}
    </div>
  )
}

function formatStatus(game: Game): string {
  switch (game.status) {
    case 'IN_PROGRESS':
      return game.score?.period
        ? `${game.score.period} - ${game.score.timeRemaining || ''}`
        : 'Live'
    case 'FINAL':
      return 'Final'
    case 'SCHEDULED':
      return formatGameTime(game.scheduledStart)
    case 'POSTPONED':
      return 'Postponed'
    case 'CANCELLED':
      return 'Cancelled'
    default:
      return game.status
  }
}

function formatGameTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })
}
